import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { stylistAPI } from '../../services/api';
import StylistLayout from '../../components/StylistLayout';
import { WardrobeTab, LooksTab, CapsulesTab, RecommendationsTab, RequestsTab } from './components';
import {
  AddItemModal,
  AddLookModal,
  AddRecommendationModal,
  AddLooksModal,
  SubscriptionModal,
  EditClientModal,
  EditItemModal,
  EditLookModal,
  EditRecommendationModal,
  CreateCapsuleModal,
  CapsuleDetailModal,
  CreateRequestModal,
  RequestDetailModal
} from './components/modals';
import { 
  Loader2, 
  ArrowLeft,
  Shirt,
  Sparkles,
  PackageOpen,
  ShoppingBag,
  MessageSquare,
  Plus,
  X,
  Link as LinkIcon,
  Copy,
  Check,
  Edit2
} from 'lucide-react';

const StylistClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [clientData, setClientData] = useState(null);
  const [wardrobe, setWardrobe] = useState([]);
  const [looks, setLooks] = useState([]);
  const [capsules, setCapsules] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests');
  
  // Modals
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showAddLookModal, setShowAddLookModal] = useState(false);
  const [showAddRecommendationModal, setShowAddRecommendationModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showAddLooksModal, setShowAddLooksModal] = useState(false);
  const [magicLinkModal, setMagicLinkModal] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Edit modals
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingLook, setEditingLook] = useState(null);
  const [editingRecommendation, setEditingRecommendation] = useState(null);
  
  // Capsule modals
  const [showCreateCapsuleModal, setShowCreateCapsuleModal] = useState(false);
  const [viewingCapsuleId, setViewingCapsuleId] = useState(null);
  const [createLookForCapsule, setCreateLookForCapsule] = useState(null);
  
  // Request modals
  const [showCreateRequestModal, setShowCreateRequestModal] = useState(false);
  const [viewingRequestId, setViewingRequestId] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, [clientId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [details, wardrobeData, looksData, capsulesData, recsData, requestsData] = await Promise.all([
        stylistAPI.getClientDetails(clientId),
        stylistAPI.getWardrobe(clientId),
        stylistAPI.getLooks(clientId),
        stylistAPI.getCapsules(clientId),
        stylistAPI.getRecommendations(clientId),
        stylistAPI.getClientRequests(clientId)
      ]);
      setClientData(details);
      setWardrobe(wardrobeData);
      setLooks(looksData);
      setCapsules(capsulesData);
      setRecommendations(recsData);
      setRequests(requestsData);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const requestsData = await stylistAPI.getClientRequests(clientId);
      setRequests(requestsData);
    } catch (err) {
      console.error('Error fetching requests:', err);
    }
  };

  const fetchCapsules = async () => {
    try {
      const capsulesData = await stylistAPI.getCapsules(clientId);
      setCapsules(capsulesData);
    } catch (err) {
      console.error('Error fetching capsules:', err);
    }
  };

  const handleGenerateMagicLink = async () => {
    try {
      const response = await stylistAPI.generateMagicLink(clientId);
      const fullUrl = `${window.location.origin}${response.magic_link}`;
      setMagicLinkModal({
        clientName: clientData.client.name,
        link: fullUrl
      });
    } catch (err) {
      console.error('Error generating magic link:', err);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(magicLinkModal.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying:', err);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Удалить эту вещь?')) return;
    try {
      await stylistAPI.deleteWardrobeItem(itemId);
      setWardrobe(wardrobe.filter(item => item.id !== itemId));
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  const handleDeleteLook = async (lookId) => {
    if (!window.confirm('Удалить этот образ?')) return;
    try {
      await stylistAPI.deleteLook(lookId);
      setLooks(looks.filter(look => look.id !== lookId));
    } catch (err) {
      console.error('Error deleting look:', err);
    }
  };

  const handleDeleteRecommendation = async (recId) => {
    if (!window.confirm('Удалить эту рекомендацию?')) return;
    try {
      await stylistAPI.deleteRecommendation(recId);
      setRecommendations(recommendations.filter(rec => rec.id !== recId));
    } catch (err) {
      console.error('Error deleting recommendation:', err);
    }
  };

  // Capsule handlers
  const handleDeleteCapsule = async (capsuleId) => {
    if (!window.confirm('Удалить эту капсулу?')) return;
    try {
      await stylistAPI.deleteCapsule(capsuleId);
      setCapsules(capsules.filter(c => c.id !== capsuleId));
    } catch (err) {
      console.error('Error deleting capsule:', err);
    }
  };

  const handlePublishCapsule = async (capsuleId) => {
    try {
      await stylistAPI.publishCapsule(capsuleId);
      fetchCapsules();
    } catch (err) {
      console.error('Error publishing capsule:', err);
    }
  };

  const handleArchiveCapsule = async (capsuleId) => {
    try {
      await stylistAPI.archiveCapsule(capsuleId);
      fetchCapsules();
    } catch (err) {
      console.error('Error archiving capsule:', err);
    }
  };

  const handleCreateLookForCapsule = (capsuleId) => {
    setCreateLookForCapsule(capsuleId);
    setViewingCapsuleId(null);
    setShowAddLookModal(true);
  };

  if (loading) {
    return (
      <StylistLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
          <Loader2 size={32} style={{ color: 'var(--accent-gold)', animation: 'spin 1s linear infinite' }} />
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </StylistLayout>
    );
  }

  if (!clientData) {
    return (
      <StylistLayout>
        <p>Клиент не найден</p>
      </StylistLayout>
    );
  }

  const { client, subscription, stats } = clientData;

  const tabs = [
    { id: 'requests', label: 'Запросы', icon: MessageSquare, count: requests.length },
    { id: 'wardrobe', label: 'Гардероб', icon: Shirt, count: wardrobe.length },
    { id: 'looks', label: 'Образы', icon: Sparkles, count: looks.length },
    { id: 'capsules', label: 'Капсулы', icon: PackageOpen, count: capsules.length },
    { id: 'recommendations', label: 'Покупки', icon: ShoppingBag, count: recommendations.length },
  ];

  return (
    <StylistLayout>
      <div style={{ maxWidth: '1200px' }} data-testid="client-detail-page">
        {/* Back Button */}
        <button
          onClick={() => navigate('/stylist')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-s)',
            background: 'none',
            border: 'none',
            fontFamily: 'var(--font-body)',
            fontSize: '0.8125rem',
            color: 'var(--mid-gray)',
            cursor: 'pointer',
            marginBottom: 'var(--space-l)',
            padding: 0
          }}
        >
          <ArrowLeft size={16} />
          Все клиенты
        </button>

        {/* Client Header */}
        <div style={{
          backgroundColor: 'var(--white)',
          padding: 'var(--space-xl)',
          marginBottom: 'var(--space-xl)',
          border: '1px solid var(--soft-gray)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 'var(--space-l)'
          }}>
            <div>
              <h1 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
                fontWeight: 300,
                marginBottom: 'var(--space-s)'
              }}>
                {client.name}
              </h1>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.875rem',
                color: 'var(--mid-gray)'
              }}>
                {client.city} {client.telegram_username && `• ${client.telegram_username}`}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-m)', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowEditClientModal(true)}
                data-testid="edit-client-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-s)',
                  padding: '10px 16px',
                  backgroundColor: 'var(--cream)',
                  border: 'none',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  cursor: 'pointer'
                }}
              >
                <Edit2 size={16} />
                Редактировать
              </button>
              
              <button
                onClick={handleGenerateMagicLink}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-s)',
                  padding: '10px 16px',
                  backgroundColor: 'var(--cream)',
                  border: 'none',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  cursor: 'pointer'
                }}
              >
                <LinkIcon size={16} />
                Ссылка для входа
              </button>
              
              <button
                onClick={() => setShowSubscriptionModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-s)',
                  padding: '10px 16px',
                  backgroundColor: 'var(--black)',
                  color: 'var(--white)',
                  border: 'none',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  cursor: 'pointer'
                }}
              >
                <Plus size={16} />
                Оформить подписку
              </button>
            </div>
          </div>

          {/* Subscription Section */}
          <div style={{
            marginTop: 'var(--space-l)',
            paddingTop: 'var(--space-l)',
            borderTop: '1px solid var(--soft-gray)'
          }}>
            {subscription ? (
              <div>
                {/* Subscription Stats - New Design */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 'var(--space-l)',
                  marginBottom: 'var(--space-l)' 
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xl)' }}>
                    <div>
                      <span style={{ 
                        fontFamily: 'var(--font-body)', 
                        fontSize: '0.875rem', 
                        color: 'var(--charcoal)', 
                        textTransform: 'uppercase', 
                        letterSpacing: '0.5px',
                        fontWeight: 500
                      }}>
                        Осталось действий
                      </span>
                      <p style={{ 
                        fontFamily: 'var(--font-display)', 
                        fontSize: '3rem', 
                        marginTop: '8px',
                        fontWeight: 600,
                        color: (subscription.remaining || subscription.remaining_look_requests || 0) > 0 ? '#2d5a3d' : '#dc2626',
                        lineHeight: 1
                      }}>
                        {subscription.remaining || subscription.remaining_look_requests || 0}
                      </p>
                    </div>
                    <div style={{
                      padding: 'var(--space-m) var(--space-l)',
                      backgroundColor: 'var(--white)',
                      border: '1px solid var(--soft-gray)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px'
                    }}>
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.9375rem', color: 'var(--charcoal)' }}>
                        Всего: <strong>{subscription.total || subscription.total_look_requests || 0}</strong>
                      </span>
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.9375rem', color: 'var(--charcoal)' }}>
                        Использовано: <strong>{subscription.used || subscription.used_look_requests || 0}</strong>
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowAddLooksModal(true)}
                    data-testid="add-actions-btn"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-s)',
                      padding: '12px 20px',
                      backgroundColor: 'var(--black)',
                      color: 'var(--white)',
                      border: 'none',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.9375rem',
                      cursor: 'pointer'
                    }}
                  >
                    <Plus size={18} />
                    Пополнить
                  </button>
                </div>

                {/* Stats Row */}
                <div style={{
                  display: 'flex',
                  gap: 'var(--space-xxl)',
                  padding: 'var(--space-l)',
                  backgroundColor: 'var(--white)',
                  border: '1px solid var(--soft-gray)'
                }}>
                  <div>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--mid-gray)', fontWeight: 500 }}>
                      Образов
                    </span>
                    <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginTop: '4px', fontWeight: 600, color: 'var(--charcoal)' }}>
                      {looks.length}
                    </p>
                  </div>
                  <div>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--mid-gray)', fontWeight: 500 }}>
                      Рекомендаций
                    </span>
                    <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginTop: '4px', fontWeight: 600, color: 'var(--charcoal)' }}>
                      {recommendations.length}
                    </p>
                  </div>
                  <div>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--mid-gray)', fontWeight: 500 }}>
                      Запросов в работе
                    </span>
                    <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginTop: '4px', fontWeight: 600, color: 'var(--charcoal)' }}>
                      {requests.filter(r => r.status === 'new' || r.status === 'in_progress').length}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 'var(--space-m)'
              }}>
                <p style={{ fontFamily: 'var(--font-body)', color: 'var(--charcoal)', fontSize: '1rem' }}>
                  Нет активной подписки
                </p>
                <button
                  onClick={() => setShowSubscriptionModal(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-s)',
                    padding: '10px 16px',
                    backgroundColor: 'var(--black)',
                    color: 'var(--white)',
                    border: 'none',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    cursor: 'pointer'
                  }}
                >
                  <Plus size={16} />
                  Создать подписку
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-s)',
          marginBottom: 'var(--space-xl)',
          overflowX: 'auto',
          paddingBottom: 'var(--space-s)'
        }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-s)',
                  padding: '12px 20px',
                  backgroundColor: isActive ? 'var(--black)' : 'var(--white)',
                  color: isActive ? 'var(--white)' : 'var(--charcoal)',
                  border: '1px solid var(--soft-gray)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8125rem',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                <Icon size={16} />
                {tab.label}
                <span style={{
                  backgroundColor: isActive ? 'var(--accent-gold)' : 'var(--cream)',
                  color: isActive ? 'var(--black)' : 'var(--charcoal)',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '0.75rem'
                }}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div style={{ marginBottom: 'var(--space-xxl)' }}>
          {activeTab === 'requests' && (
            <RequestsTab 
              requests={requests}
              onAdd={() => setShowCreateRequestModal(true)}
              onView={(request) => setViewingRequestId(request.id)}
              onDelete={(requestId) => setRequests(requests.filter(r => r.id !== requestId))}
            />
          )}
          {activeTab === 'wardrobe' && (
            <WardrobeTab 
              items={wardrobe} 
              onAdd={() => setShowAddItemModal(true)}
              onDelete={handleDeleteItem}
              onEdit={(item) => setEditingItem(item)}
            />
          )}
          {activeTab === 'looks' && (
            <LooksTab 
              looks={looks} 
              wardrobeItems={wardrobe}
              onAdd={() => setShowAddLookModal(true)}
              onDelete={handleDeleteLook}
              onEdit={(look) => setEditingLook(look)}
            />
          )}
          {activeTab === 'capsules' && (
            <CapsulesTab 
              capsules={capsules}
              onAdd={() => setShowCreateCapsuleModal(true)}
              onEdit={(capsule) => setViewingCapsuleId(capsule.id)}
              onDelete={handleDeleteCapsule}
              onPublish={handlePublishCapsule}
              onArchive={handleArchiveCapsule}
              onViewDetail={(capsule) => setViewingCapsuleId(capsule.id)}
            />
          )}
          {activeTab === 'recommendations' && (
            <RecommendationsTab 
              recommendations={recommendations}
              onAdd={() => setShowAddRecommendationModal(true)}
              onEdit={(rec) => setEditingRecommendation(rec)}
              onDelete={handleDeleteRecommendation}
            />
          )}
        </div>

        {/* Modals */}
        {showAddItemModal && (
          <AddItemModal 
            clientId={clientId}
            onClose={() => setShowAddItemModal(false)}
            onAdded={(newItem) => {
              setWardrobe([newItem, ...wardrobe]);
              setShowAddItemModal(false);
            }}
          />
        )}

        {showAddLookModal && (
          <AddLookModal 
            clientId={clientId}
            wardrobeItems={wardrobe}
            requests={requests}
            onClose={() => {
              setShowAddLookModal(false);
              setCreateLookForCapsule(null);
            }}
            onAdded={async () => {
              await fetchAllData();
              setShowAddLookModal(false);
              // If creating for capsule, add the new look to it
              if (createLookForCapsule) {
                const newLooks = await stylistAPI.getLooks(clientId);
                if (newLooks.length > 0) {
                  const newLookId = newLooks[0].id;
                  await stylistAPI.addLooksToCapsule(createLookForCapsule, [newLookId]);
                }
                setViewingCapsuleId(createLookForCapsule);
                setCreateLookForCapsule(null);
              }
            }}
          />
        )}

        {showAddRecommendationModal && (
          <AddRecommendationModal 
            clientId={clientId}
            requests={requests}
            onClose={() => setShowAddRecommendationModal(false)}
            onAdded={(newRec) => {
              fetchAllData();
              setShowAddRecommendationModal(false);
            }}
          />
        )}

        {showSubscriptionModal && (
          <SubscriptionModal 
            clientId={clientId}
            currentSubscription={subscription}
            onClose={() => setShowSubscriptionModal(false)}
            onUpdated={() => {
              fetchAllData();
              setShowSubscriptionModal(false);
            }}
          />
        )}

        {showAddLooksModal && (
          <AddLooksModal 
            clientId={clientId}
            onClose={() => setShowAddLooksModal(false)}
            onAdded={(result) => {
              setClientData(prev => ({
                ...prev,
                subscription: {
                  ...prev.subscription,
                  total_look_requests: result.new_total,
                  remaining_look_requests: result.remaining
                }
              }));
              setShowAddLooksModal(false);
            }}
          />
        )}

        {magicLinkModal && (
          <div
            onClick={() => setMagicLinkModal(null)}
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 'var(--space-l)',
              zIndex: 1000
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'var(--white)',
                padding: 'var(--space-xxl)',
                maxWidth: '500px',
                width: '100%'
              }}
            >
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginBottom: 'var(--space-m)' }}>
                Ссылка для {magicLinkModal.clientName}
              </h3>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--mid-gray)', marginBottom: 'var(--space-l)' }}>
                Скопируйте и отправьте клиенту через мессенджер
              </p>
              <div style={{ display: 'flex', gap: 'var(--space-s)', marginBottom: 'var(--space-l)' }}>
                <input
                  type="text"
                  value={magicLinkModal.link}
                  readOnly
                  style={{
                    flex: 1,
                    padding: '12px',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8125rem',
                    border: '1px solid var(--soft-gray)',
                    backgroundColor: 'var(--cream)'
                  }}
                />
                <button
                  onClick={handleCopyLink}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: copied ? '#22c55e' : 'var(--black)',
                    color: 'var(--white)',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-s)'
                  }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>
              <button
                onClick={() => setMagicLinkModal(null)}
                style={{
                  width: '100%',
                  padding: '14px',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--soft-gray)',
                  fontFamily: 'var(--font-body)',
                  cursor: 'pointer'
                }}
              >
                Закрыть
              </button>
            </div>
          </div>
        )}

        {/* Edit Client Modal */}
        {showEditClientModal && (
          <EditClientModal 
            client={client}
            onClose={() => setShowEditClientModal(false)}
            onUpdated={(updatedClient) => {
              setClientData({ ...clientData, client: updatedClient });
              setShowEditClientModal(false);
            }}
          />
        )}

        {/* Edit Item Modal */}
        {editingItem && (
          <EditItemModal 
            item={editingItem}
            onClose={() => setEditingItem(null)}
            onUpdated={(updatedItem) => {
              setWardrobe(wardrobe.map(item => item.id === updatedItem.id ? updatedItem : item));
              setEditingItem(null);
            }}
          />
        )}

        {/* Edit Look Modal */}
        {editingLook && (
          <EditLookModal 
            look={editingLook}
            wardrobeItems={wardrobe}
            onClose={() => setEditingLook(null)}
            onUpdated={() => {
              fetchAllData();
              setEditingLook(null);
            }}
          />
        )}

        {/* Edit Recommendation Modal */}
        {editingRecommendation && (
          <EditRecommendationModal 
            recommendation={editingRecommendation}
            onClose={() => setEditingRecommendation(null)}
            onUpdated={(updatedRec) => {
              setRecommendations(recommendations.map(rec => 
                rec.id === updatedRec.id ? updatedRec : rec
              ));
              setEditingRecommendation(null);
            }}
          />
        )}

        {/* Create Capsule Modal */}
        {showCreateCapsuleModal && (
          <CreateCapsuleModal
            clientId={clientId}
            onClose={() => setShowCreateCapsuleModal(false)}
            onCreated={(capsuleId) => {
              setShowCreateCapsuleModal(false);
              fetchCapsules();
              setViewingCapsuleId(capsuleId);
            }}
          />
        )}

        {/* Capsule Detail Modal */}
        {viewingCapsuleId && (
          <CapsuleDetailModal
            capsuleId={viewingCapsuleId}
            clientId={clientId}
            allWardrobeItems={wardrobe}
            allLooks={looks}
            onClose={() => setViewingCapsuleId(null)}
            onUpdated={fetchCapsules}
            onCreateLook={handleCreateLookForCapsule}
          />
        )}

        {/* Create Request Modal */}
        {showCreateRequestModal && (
          <CreateRequestModal
            clientId={clientId}
            onClose={() => setShowCreateRequestModal(false)}
            onCreated={(newRequest) => {
              setRequests([newRequest, ...requests]);
              setShowCreateRequestModal(false);
            }}
          />
        )}

        {/* Request Detail Modal */}
        {viewingRequestId && (
          <RequestDetailModal
            requestId={viewingRequestId}
            onClose={() => setViewingRequestId(null)}
            onUpdated={fetchRequests}
            onDeleted={(deletedId) => {
              setRequests(requests.filter(r => r.id !== deletedId));
            }}
          />
        )}
      </div>
    </StylistLayout>
  );
};

export default StylistClientDetail;
